import { useCallback, useMemo } from "react";

import { useTranslation } from "react-i18next";

import { BuildingIcon } from "lucide-react";

import { InvitationOfType, UseInvitationOptions } from "simplydo/interfaces";

import {
  acceptAuthenticatedInvitation,
  rejectAuthenticatedInvitation,
  removeAuthenticatedInvitation,
  toastError,
  toastMessage,
  toastSuccess,
  withInvitationPrompt,
} from "./internal";
import type { InvitationViewModel } from "./types";
import { emptyInvitationViewModel } from "./types";

export const useIdeaBusinessProfileInvitation = (
  invitation: InvitationOfType<"ideaBusinessProfile"> | undefined,
  options?: UseInvitationOptions,
): InvitationViewModel => {
  const { t } = useTranslation();

  const profile = invitation?.ideaBusinessProfile;
  const profileId = profile?._id;
  const profileName = profile?.name || t("invitations.deletedProfile");
  const profileLink = profileId ? `?businessProfileId=${profileId}` : null;
  const inviteeName =
    invitation?.inviteeUser?.profile?.fullName || invitation?.invitee;
  const canAccept = !!invitation?.canAccept && !!profile;
  const createdByCurrentUser = !!invitation?.createdByCurrentUser;

  const acceptInvite = useCallback(() => {
    if (!invitation) {
      return;
    }
    withInvitationPrompt(
      options,
      t("invitations.businessProfile.acceptTitle"),
      t("invitations.businessProfile.acceptMessage", { name: profile?.name }),
      () => {
        acceptAuthenticatedInvitation(
          invitation,
          () => toastSuccess(t("invitations.businessProfile.accepted")),
          () => toastError(t("invitations.businessProfile.acceptError")),
        );
      },
    );
  }, [invitation, options, profile?.name, t]);

  const rejectInvite = useCallback(() => {
    if (!invitation) {
      return;
    }
    withInvitationPrompt(
      options,
      t("invitations.businessProfile.rejectTitle"),
      t("invitations.businessProfile.rejectMessage", { name: profileName }),
      () => {
        rejectAuthenticatedInvitation(
          invitation,
          () => toastMessage(t("invitations.rejected")),
          () => toastError(t("invitations.rejectError")),
        );
      },
    );
  }, [invitation, options, profileName, t]);

  const removeInvite = useCallback(() => {
    if (!invitation) {
      return;
    }
    withInvitationPrompt(
      options,
      t("invitations.businessProfile.removeTitle"),
      t("invitations.businessProfile.removeMessage", { name: profileName }),
      () => {
        removeAuthenticatedInvitation(
          invitation,
          () => toastMessage(t("invitations.removed")),
          () => toastError(t("invitations.removeError")),
        );
      },
    );
  }, [invitation, options, profileName, t]);

  return useMemo(() => {
    if (!invitation) {
      return emptyInvitationViewModel();
    }

    return {
      icon: BuildingIcon,
      link: profileLink,
      isInvalid: !profile,
      text: {
        title: profileName,
        fullTitle: t("invitations.businessProfile.fullPageTitle", {
          name: profileName,
        }),
        youInvited: t("invitations.businessProfile.creatorPostText", {
          inviteeName,
        }),
        hasInvitedYou: t("invitations.businessProfile.receivedPostText"),
        dismiss: createdByCurrentUser
          ? t("invitations.remove")
          : t("invitations.reject"),
        invalid: t("invitations.businessProfile.invalidMessage"),
        ...(profileLink ? { goTo: t("invitations.businessProfile.view") } : {}),
      },
      acceptInvite: canAccept ? acceptInvite : undefined,
      rejectInvite: !createdByCurrentUser ? rejectInvite : undefined,
      removeInvite: createdByCurrentUser ? removeInvite : undefined,
    };
  }, [
    acceptInvite,
    canAccept,
    createdByCurrentUser,
    invitation,
    inviteeName,
    profile,
    profileLink,
    profileName,
    removeInvite,
    rejectInvite,
    t,
  ]);
};
